import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { newDateWithCurrentTimezone } from "@/utils";
import {
  getDateRange,
  getMentionViewDateRanges,
} from "@/views/MentionsView/MentionLayout/ranges";
import { differenceInCalendarDays } from "date-fns";

const FILTER_TYPE = {
  insight_types: "insights",
  channel_type: "media_type",
  channel_name: "source",
  sentiment: "sentiment",
  language: "language",
  mention_kind: "mention_type",
  min_reach_threshold: "reach",
  freetext: "free_text",
} as const;

type TrackedFilterKey = keyof typeof FILTER_TYPE;
type FilterType = (typeof FILTER_TYPE)[TrackedFilterKey];

const FILTER_TYPES_WITH_VALUE: FilterType[] = [
  "insights",
  "media_type",
  "sentiment",
];

type TrackingEvent = {
  event: string;
  properties: Record<string, unknown>;
};

function toArray(value: unknown): string[] {
  if (value === undefined || value === null) {
    return [];
  }

  return (Array.isArray(value) ? value : [value]).map(String);
}

function getDateEvent(
  patch: Partial<SearchFilterV4>,
  filters: Partial<SearchFilterV4>,
  searchStartDate?: string
): TrackingEvent | undefined {
  const { start_date: startDate, end_date: endDate } = patch;

  if (!startDate || !endDate) {
    return undefined;
  }

  if (startDate === filters.start_date && endDate === filters.end_date) {
    return undefined;
  }

  const range = getDateRange(
    startDate,
    endDate,
    getMentionViewDateRanges(searchStartDate)
  );

  if (range) {
    return {
      event: EVENT.DATE_PRESET_CHANGE,
      properties: { date_range: range.identifier },
    };
  }

  return {
    event: EVENT.DATE_PRESET_CHANGE,
    properties: {
      date_range: "custom",
      start_date: startDate,
      end_date: endDate,
      range_days:
        differenceInCalendarDays(
          newDateWithCurrentTimezone(endDate),
          newDateWithCurrentTimezone(startDate)
        ) + 1,
    },
  };
}

function getFilterEvent(
  key: TrackedFilterKey,
  patch: Partial<SearchFilterV4>,
  filters: Partial<SearchFilterV4>
): TrackingEvent | undefined {
  const filter_type = FILTER_TYPE[key];

  if (key === "freetext") {
    if (!patch.freetext || patch.freetext === filters.freetext) {
      return undefined;
    }

    const filter_value = patch.freetext.trim();

    return {
      event: EVENT.MENTIONS_FILTERED,
      properties: { filter_type, ...(filter_value ? { filter_value } : {}) },
    };
  }

  if (key === "min_reach_threshold") {
    return patch.min_reach_threshold !== undefined &&
      patch.min_reach_threshold !== filters.min_reach_threshold
      ? { event: EVENT.MENTIONS_FILTERED, properties: { filter_type } }
      : undefined;
  }

  const current = toArray(filters[key]);
  const added = toArray(patch[key]).filter((value) => !current.includes(value));

  if (added.length === 0) {
    return undefined;
  }

  return {
    event: EVENT.MENTIONS_FILTERED,
    properties: {
      filter_type,
      ...(FILTER_TYPES_WITH_VALUE.includes(filter_type)
        ? { filter_value: added.length === 1 ? added[0] : added }
        : {}),
    },
  };
}

export function getFilterTrackingEvents(
  patch: Partial<SearchFilterV4>,
  filters: Partial<SearchFilterV4>,
  searchStartDate?: string
): TrackingEvent[] {
  const events: TrackingEvent[] = [];

  const dateEvent = getDateEvent(patch, filters, searchStartDate);

  if (dateEvent) {
    events.push(dateEvent);
  }

  for (const key of Object.keys(FILTER_TYPE) as TrackedFilterKey[]) {
    if (!(key in patch)) {
      continue;
    }

    const event = getFilterEvent(key, patch, filters);

    if (event) {
      events.push(event);
    }
  }

  return events;
}

export function trackFilterChange(
  patch: Partial<SearchFilterV4>,
  filters: Partial<SearchFilterV4>,
  searchStartDate?: string
) {
  for (const { event, properties } of getFilterTrackingEvents(
    patch,
    filters,
    searchStartDate
  )) {
    postTrackingData(event, properties);
  }
}
